import { el, svg } from "../core/dom";
import { styles } from "../styles/modal";
import type { Settings } from "../api/types";
import type { ResolvedColors } from "./colors";

export type AgeVerificationRefs = {
  container: HTMLElement;
};

export function renderAgeVerification(options: {
  settings?: Partial<Settings>;
  colors: ResolvedColors;
  isMobile: boolean;
  logoUrl: string;
  onYes: () => void;
  onNo: () => void;
  onClose: () => void;
}): AgeVerificationRefs {
  const { settings, colors, isMobile, logoUrl, onYes, onNo, onClose } = options;

  const headingColor = settings?.headingColor || "#101828";
  const textColor = colors.text;
  const font = colors.font;

  // ─── Top Section (matches React: logo+title left, X button right) ───
  const logo = el("img", { src: logoUrl, style: styles.logo, alt: "Redacto Logo" });

  const title = el("h2", {
    id: "age-verification-title",
    style: {
      ...styles.title,
      fontSize: isMobile ? "16px" : "18px",
      color: headingColor,
      fontFamily: font,
    },
  });
  title.textContent = "Age Verification Required";

  const topLeft = el("div", { style: styles.topLeft }, logo, title);

  const closeBtn = el("button", {
    style: {
      background: "none",
      border: "none",
      color: headingColor,
      cursor: "pointer",
      padding: isMobile ? "2px" : "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    type: "button",
    "aria-label": "Close modal",
    onClick: onClose,
  });
  const closeSvg = svg(
    "svg",
    {
      width: isMobile ? "18" : "20",
      height: isMobile ? "18" : "20",
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "aria-hidden": "true",
    },
    svg("path", {
      d: "M15 5L5 15M5 5L15 15",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
    })
  );
  closeBtn.appendChild(closeSvg);

  const topSection = el("div", {
    style: { ...styles.topSection, color: textColor, fontFamily: font },
  }, topLeft, closeBtn);

  // ─── Middle Section ───
  const description = el("p", {
    style: {
      ...styles.privacyText,
      fontSize: isMobile ? "14px" : "16px",
      color: textColor,
      fontFamily: font,
      marginBottom: "16px",
    },
  });
  description.textContent = "To proceed with this consent form, we need to verify your age.";

  const question = el("p", {
    style: {
      ...styles.subTitle,
      color: textColor,
      fontFamily: font,
      marginBottom: "24px",
      textAlign: "left",
    },
  });
  question.textContent = "Are you 18 years of age or older?";

  const middleSection = el("div", {
    style: { ...styles.middleSection, color: textColor, fontFamily: font },
  }, description, question);

  // ─── Bottom Section (Yes first, No second — matches React) ───
  const btnBase = {
    ...styles.button,
    fontSize: isMobile ? "14px" : "16px",
    padding: isMobile ? "10px 20px" : "9px 45px",
  };

  const yesBtn = el("button", {
    style: {
      ...btnBase,
      ...styles.acceptButton,
      backgroundColor: colors.acceptBg,
      color: colors.acceptText,
      borderRadius: colors.borderRadius,
    },
    type: "button",
    "aria-label": "Confirm that I am 18 years of age or older",
    onClick: onYes,
  });
  yesBtn.textContent = "Yes, I am 18 or older";

  const noBtn = el("button", {
    style: {
      ...btnBase,
      ...styles.cancelButton,
      backgroundColor: colors.declineBg,
      color: colors.declineText,
      borderColor: colors.declineBorder,
      borderRadius: colors.borderRadius,
    },
    type: "button",
    "aria-label": "Indicate that I am under 18 years of age",
    onClick: onNo,
  });
  noBtn.textContent = "No, I am under 18";

  const bottomSection = el("div", {
    style: { ...styles.bottomSection, color: textColor, fontFamily: font },
  }, yesBtn, noBtn);

  // ─── Assemble (wrapped in content div like modal) ───
  const container = el("div", { style: styles.content }, topSection, middleSection, bottomSection);

  return { container };
}
